import {
  InvoicePaymentStatusEnum,
  InvoiceSourceTypeEnum,
} from "@/__generated__/graphql";
import { Button, Card, CardContent, View } from "@/components/ui";
import { PaymentStatusBadge } from "./payment-status-badge";
import type { Invoice } from "@/types/invoice";
import { Divider } from "../../divider";
import { formatPrice } from "@/lib/helpers/number";
import { formatDate, parseDate } from "@/lib/helpers/date";
import { Typography } from "../../typography";
import type { PackageDepartmentEnum } from "@/__generated__/graphql";

export type InvoiceCardData = {
  id: string;
  label: string;
  amount: number;
  balance: number;
  issueDate: Date;
  dueDate: Date;
  paymentStatus: InvoicePaymentStatusEnum;
  department: PackageDepartmentEnum[];
  sourceType: InvoiceSourceTypeEnum;
};

export function mapToInvoiceCard(invoice: Invoice): InvoiceCardData {
  return {
    id: invoice.id,
    label: invoice.label ?? "-",
    amount: invoice.totalAmount,
    balance: invoice.totalAmount - invoice.paidAmount,
    issueDate: invoice.issueDate ? parseDate(invoice.issueDate) : new Date(0),
    dueDate: invoice.dueDate ? parseDate(invoice.dueDate) : new Date(0),
    paymentStatus: invoice.paymentStatus,
    department: invoice.departments ?? [],
    sourceType:
      invoice.sourceType ?? InvoiceSourceTypeEnum.LineItemTransactions,
  };
}

export function InvoiceCard({
  id,
  label,
  amount,
  balance,
  issueDate,
  dueDate,
  paymentStatus,
  onClick,
  onPay,
}: InvoiceCardData & { onClick(): void; onPay(): void }) {
  const paidInvoice = paymentStatus === InvoicePaymentStatusEnum.Paid;
  const disabled = paymentStatus === InvoicePaymentStatusEnum.StripeProcessing;
  const invoiceTitle = label.split(" - ")[0];
  return (
    <Card key={id}>
      <CardContent className="flex flex-col">
        <View className="flex w-full flex-1 flex-row" onClick={onClick}>
          <View className="flex-[2]">
            <View className="mb-2 flex flex-row items-center gap-2">
              <Typography variant="body-lg">{invoiceTitle}</Typography>
              <PaymentStatusBadge
                balance={balance}
                amount={amount}
                dueDate={dueDate}
                status={paymentStatus}
              />
            </View>
            <View className="flex flex-row gap-4">
              <View>
                <Typography variant="body-sm" color="foreground-intermediate">
                  Issue On
                </Typography>
                <Typography variant="body-md">
                  {formatDate(issueDate, "MMM dd, yyyy")}
                </Typography>
              </View>
              <View>
                <Typography variant="body-sm" color="foreground-intermediate">
                  Due On
                </Typography>
                <Typography variant="body-md">
                  {formatDate(dueDate, "MMM dd, yyyy")}
                </Typography>
              </View>
            </View>
          </View>
          <Divider vertical />
          <View className="ml-4 flex flex-col gap-1 md:ml-12">
            <Typography
              variant="body-sm"
              color="foreground-intermediate"
              align="right"
            >
              Total Amount
            </Typography>
            <Typography variant="body-lg" align="right" color="brand-primary">
              {formatPrice(balance)}
            </Typography>
          </View>
        </View>
      </CardContent>
      <View className="flex w-full flex-1 flex-row gap-2 p-3 pb-4 md:ml-auto md:max-w-96">
        <Button
          variant="secondary"
          color="CTA2"
          fullWidth="full"
          size="sm"
          onClick={onClick}
          children="View Details"
        />
        {!paidInvoice && (
          <Button
            variant="primary"
            color="CTA"
            disabled={disabled}
            size="sm"
            fullWidth="full"
            children="Pay"
            onClick={onPay}
          />
        )}
      </View>
    </Card>
  );
}
